import styled from 'styled-components'
import useTranslation from '../../../../hooks/useTranslation'
import { change, measuresOf, tone } from './measures'
import { widgetFont } from '../../widgetTypography'

/*
 * The numbers around a chart: the headline above it and up to three KPIs
 * below. Both are configured the same way —
 *
 *     { id, label, value: <expression>, format, change: 'percent' | 'points' | 'none',
 *       favorable: 'up' | 'down' | 'neutral' }
 *
 * — and both compare with the previous period when the widget does. Whether a
 * rise is good is the config's call: more credit notes is a rise nobody wants.
 * The arrow shows the direction and the colour the verdict, so the two never
 * have to be read from one signal, and neither from colour alone.
 */

// The format an expression is shown in when the config does not say
export const formatOf = (expr, measures = {}, fallback = 'number') => {
    if (expr && typeof expr === 'object' && ('percentOf' in expr || 'shareOf' in expr)) return 'percent'
    const first = measuresOf(expr)[0]
    return measures[first]?.format ?? fallback
}

// A KPI resolved against the data: the value, its text, its change
export const resolveKpi = (kpi, data, measures) => {
    const format = kpi.format ?? formatOf(kpi.value, measures)
    const current = data.current?.total(kpi.value) ?? null
    const previous = data.previous?.total(kpi.value) ?? null
    const mode = kpi.change ?? (format === 'percent' ? 'points' : 'percent')
    const delta = mode === 'none' || !data.previous ? null : change(current, previous, mode)
    return { ...kpi, format, current, previous, delta, tone: tone(delta, kpi.favorable) }
}

const toneColor = (theme, t) =>
    t === 'good' ? theme.emotive.success.color : t === 'bad' ? theme.emotive.error.color : theme.font.label

const DeltaText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    ${p => p.$size === 'large' ? widgetFont.label : widgetFont.caption}
    font-weight: 600;
    white-space: nowrap;
    color: ${p => toneColor(p.theme, p.$tone)};
`

const Arrow = ({ up }) => (
    <svg width="0.75em" height="0.75em" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        <path d={up ? 'M6 1.5 L10.5 7 H7.4 V10.5 H4.6 V7 H1.5 Z' : 'M6 10.5 L10.5 5 H7.4 V1.5 H4.6 V5 H1.5 Z'} fill="currentColor" />
    </svg>
)

export const Delta = ({ delta, kpiTone, format, size }) => {
    const { t } = useTranslation()
    if (!delta) return null
    if (delta.isNew) return <DeltaText $tone="neutral" $size={size}>{t('analytics.change.new')}</DeltaText>
    const up = delta.value > 0
    const flat = Math.abs(delta.value) < 0.05
    const text = delta.mode === 'points'
        ? `${up ? '+' : ''}${delta.value.toFixed(1)} ${t('analytics.change.points')}`
        : `${Math.abs(delta.value) >= 100 ? Math.round(Math.abs(delta.value)) : Math.abs(delta.value).toFixed(1)}%`
    return (
        <DeltaText $tone={kpiTone} $size={size} aria-label={`${up ? t('analytics.change.up') : flat ? '' : t('analytics.change.down')} ${text}`}>
            {!flat && <Arrow up={up} />}
            {text}
        </DeltaText>
    )
}

const HeadlineWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    flex-shrink: 0;
`

const HeadlineRow = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.15rem 0.6rem;
`

const HeadlineValue = styled.div`
    ${widgetFont.hero}
`

const Caption = styled.span`
    ${widgetFont.caption}
    color: ${p => p.theme.font.label};
`

/*
 * The one number a card leads with. `caption` says what it is when the title
 * alone could mislead — "average per order", not a total.
 */
export const Headline = ({ kpi, formatValue, compareLabel }) => {
    const { t } = useTranslation()
    if (!kpi) return null
    return (
        <HeadlineWrap>
            <HeadlineRow>
                <HeadlineValue>{formatValue(kpi.current, kpi.format)}</HeadlineValue>
                {kpi.delta && (
                    <span>
                        <Delta delta={kpi.delta} kpiTone={kpi.tone} format={kpi.format} size="large" />
                        {' '}<Caption>{t(compareLabel)}</Caption>
                    </span>
                )}
            </HeadlineRow>
            {kpi.label && <Caption>{t(kpi.label)}</Caption>}
        </HeadlineWrap>
    )
}

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(${p => p.$count}, minmax(0, 1fr));
    flex-shrink: 0;
    border-top: 1px solid ${p => p.theme.table.border};
    padding-top: 0.5rem;

    @container analytics (max-width: 260px) {
        grid-template-columns: 1fr;
        gap: 0.35rem;
    }
`

const Block = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
    padding: 0 0.6rem;

    &:first-child { padding-left: 0; }
    & + & { border-left: 1px solid ${p => p.theme.table.border}; }

    @container analytics (max-width: 260px) {
        flex-direction: row;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0;
        & + & { border-left: none; }
    }
`

const BlockLabel = styled.span`
    ${widgetFont.label}
    color: ${p => p.theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const BlockValue = styled.span`
    ${widgetFont.value}
    white-space: nowrap;
`

const BlockFigures = styled.span`
    display: flex;
    flex-direction: column;
    gap: 0.05rem;

    @container analytics (max-width: 260px) {
        flex-direction: row;
        align-items: baseline;
        gap: 0.4rem;
    }
`

export const KpiRow = ({ kpis, formatValue }) => {
    const { t } = useTranslation()
    if (!kpis?.length) return null
    const shown = kpis.slice(0, 3)
    return (
        <Row $count={shown.length}>
            {shown.map(kpi => (
                <Block key={kpi.id}>
                    <BlockLabel title={t(kpi.label)}>{t(kpi.label)}</BlockLabel>
                    <BlockFigures>
                        <BlockValue>{formatValue(kpi.current, kpi.format)}</BlockValue>
                        <Delta delta={kpi.delta} kpiTone={kpi.tone} format={kpi.format} />
                    </BlockFigures>
                </Block>
            ))}
        </Row>
    )
}
